/*
 * Tool A on /law-firm-seo-audit and the UK solicitor pages: instant law firm website check.
 * Static heading/intro/input render in the prerendered HTML; only results render client-side.
 * All analysis runs in the runWebsiteCheck server function (PageSpeed + homepage parse).
 * No storage, no cookies; analytics events go through the existing gtag/GTM setup.
 * Visual design: "command centre" navy card; copy, scoring and events unchanged.
 *
 * The homepage hero bar (HeroWebsiteCheckBar) reuses useWebsiteCheck and the
 * exported progress/error/results panels so it can run the same check in place.
 */
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { runWebsiteCheck } from "@/lib/website-check.functions";

type CheckStatus = "pass" | "warn" | "fail" | "unknown";
type CheckRow = { key: string; label: string; status: CheckStatus; value: string };
type CheckOk = {
  status: "ok";
  url: string;
  score: number;
  band: string;
  pagespeed: boolean;
  cached: boolean;
  checks: CheckRow[];
};
type CheckErr = { status: "error"; errorType: "invalid" | "unreachable" | "rate_limited" };
type CheckResponse = CheckOk | CheckErr;

export type WebsiteCheckErrorType = CheckErr["errorType"];

const STEPS = [
  "Testing mobile speed…",
  "Measuring Core Web Vitals…",
  "Checking security…",
  "Reading page tags and schema…",
];
const STEP_DONE_LABELS = [
  "Mobile speed tested",
  "Core Web Vitals measured",
  "Checking security…",
  "Reading page tags and schema…",
];
/* Approximate % thresholds per step: the HTML checks (security, tags, schema)
 * finish server-side before PageSpeed, so they complete earlier. */
const STEP_THRESHOLDS = [65, 85, 25, 45];

const CHIP_ICONS = [
  /* lightning */ "M13 2 3 14h7l-1 8 10-12h-7l1-8z",
  /* bar chart */ "M3 3v18h18M8 17V9m4 8V5m4 12v-6",
  /* lock */ "M7 11V7a5 5 0 0 1 10 0v4M5 11h14v10H5z",
  /* tag */ "M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8zM7.5 7.5h.01",
  /* puzzle */ "M14 3v3a2 2 0 1 0 4 0h3v4h-3a2 2 0 1 0 0 4h3v4h-4v3h-4v-3a2 2 0 1 0-4 0H6v-4H3v-4h3a2 2 0 1 0 0-4V3h4z",
  /* phone */ "M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.13.96.36 1.9.7 2.8a2 2 0 0 1-.45 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.45c.9.34 1.84.57 2.8.7A2 2 0 0 1 22 16.9z",
];

const FEATURE_CHIPS = [
  "Mobile & desktop speed",
  "Core Web Vitals",
  "HTTPS",
  "Title, meta & H1",
  "Legal schema",
  "Click-to-call",
];

const EXPLANATIONS: Record<string, string> = {
  mobile_speed:
    "Most people searching for a lawyer use a phone. Slow mobile pages lose callers before they read anything.",
  desktop_speed: "Office-based clients and referral sources still check firms on desktop.",
  lcp: "How fast the main content appears. Slow loading makes visitors leave.",
  inp: "How quickly your page responds when someone taps or clicks.",
  cls: "Whether the layout jumps while loading, which causes misclicks.",
  https: "A secure site is expected by visitors and by Google.",
  title:
    "Your title is the headline people see in Google. Missing or cut-off titles get fewer clicks.",
  meta_description:
    "The short summary under your title in Google results. A clear one earns more clicks.",
  single_h1: "One clear main heading helps Google understand the page.",
  schema:
    "Structured data helps Google show your firm details and understand your practice.",
  phone: "A tappable phone number makes it easy for mobile visitors to call you.",
};

const ERROR_MESSAGES: Record<WebsiteCheckErrorType, string> = {
  invalid: "Please enter a valid website address, for example yourlawfirm.com.",
  unreachable:
    "We couldn't test this site automatically. It may be blocking automated checks. Request the full audit above and a strategist will review it manually.",
  rate_limited:
    "You've run several checks in a short time. Please try again in an hour, or request the full audit above.",
};

const VALUE_COLORS: Record<CheckStatus, string> = {
  pass: "#4ade80",
  warn: "#fbbf24",
  fail: "#f87171",
  unknown: "#8e9bb0",
};

function track(event: string, params: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  const w = window as { gtag?: (...args: unknown[]) => void };
  w.gtag?.("event", event, { page_location: window.location.href, ...params });
}

function scrollToAuditForm(source: string, targetId = "audit-form") {
  track("audit_cta_click", { source });
  document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function domainOf(raw: string): string {
  const cleaned = raw.trim().replace(/^https?:\/\//i, "");
  return cleaned.split("/")[0] || cleaned;
}

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e8a93a] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f1b2d]";

function LockIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[#8e9bb0]"
    >
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/* Keeps the card height stable by animating between measured content heights. */
function AutoHeight({ dep, children }: { dep: string; children: React.ReactNode }) {
  const inner = useRef<HTMLDivElement>(null);
  const [h, setH] = useState<number | undefined>(undefined);
  useEffect(() => {
    const el = inner.current;
    if (!el) return;
    const measure = () => setH(el.scrollHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [dep]);
  return (
    <div
      style={{
        height: h === undefined ? "auto" : h,
        transition: "height 0.4s ease",
        overflow: "hidden",
      }}
    >
      <div ref={inner} className="flow-root">
        {children}
      </div>
    </div>
  );
}

function StepIcon({ state }: { state: "done" | "running" | "waiting" }) {
  if (state === "done")
    return (
      <span
        aria-label="Done"
        className="flex h-6 w-6 items-center justify-center rounded-full bg-[#1f9d55] text-xs font-bold text-white"
      >
        ✓
      </span>
    );
  if (state === "running")
    return (
      <span
        aria-label="Running"
        className="h-6 w-6 rounded-full border-2 border-[rgba(232,169,58,0.25)] border-t-[#e8a93a] animate-spin motion-reduce:animate-none"
      />
    );
  return (
    <span
      aria-label="Waiting"
      className="h-6 w-6 rounded-full border-2 border-[rgba(255,255,255,0.18)]"
    />
  );
}

function ProgressRing({ percent }: { percent: number }) {
  const r = 88;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.min(100, Math.max(0, percent)) / 100);
  return (
    <div className="relative h-40 w-40 md:h-[200px] md:w-[200px]">
      <svg
        viewBox="0 0 200 200"
        className="h-full w-full -rotate-90"
        role="progressbar"
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Check progress"
      >
        <circle cx="100" cy="100" r={r} fill="none" strokeWidth="14" stroke="rgba(255,255,255,0.08)" />
        <circle
          cx="100"
          cy="100"
          r={r}
          fill="none"
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          stroke="#e8a93a"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" aria-live="polite">
        <span className="font-display text-4xl font-bold text-white md:text-[52px]">
          {Math.round(percent)}%
        </span>
        <span className="mt-1 text-[13px] text-[#8e9bb0]">complete</span>
      </div>
    </div>
  );
}

function ScoreRing({ score, shown }: { score: number; shown: number }) {
  const r = 64;
  const c = 2 * Math.PI * r;
  const color = score >= 80 ? "#4ade80" : score >= 50 ? "#e8a93a" : "#f87171";
  const offset = c * (1 - Math.min(100, Math.max(0, shown)) / 100);
  return (
    <div className="relative h-[150px] w-[150px] shrink-0">
      <svg viewBox="0 0 150 150" className="h-full w-full -rotate-90" aria-hidden="true">
        <circle cx="75" cy="75" r={r} fill="none" strokeWidth="10" stroke="rgba(255,255,255,0.08)" />
        <circle
          cx="75"
          cy="75"
          r={r}
          fill="none"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          stroke={color}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-[44px] font-semibold leading-none text-white">
          {Math.round(shown)}
        </span>
        <span className="mt-1 text-[13px] text-[#8e9bb0]">/ 100</span>
      </div>
    </div>
  );
}

/* Shared check state machine: the full section and the homepage hero bar both use it. */
export function useWebsiteCheck() {
  const check = useServerFn(runWebsiteCheck);
  const [url, setUrl] = useState("");
  const [phase, setPhase] = useState<"idle" | "running" | "done" | "error">("idle");
  const [result, setResult] = useState<CheckOk | null>(null);
  const [error, setError] = useState<CheckErr["errorType"] | null>(null);
  const [percent, setPercent] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [shownScore, setShownScore] = useState(0);
  const rafRef = useRef<number | null>(null);
  const percentRef = useRef(0);
  const finishRef = useRef(false);

  /* Honest simulated progress: ease out toward 90% over ~22s, creep to 95%
   * after 25s, then jump to 100% when the response arrives. */
  useEffect(() => {
    if (phase !== "running") {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }
    const start = performance.now();
    let finishStart = 0;
    let finishFrom = 0;
    let doneScheduled = false;
    const tick = (now: number) => {
      const elapsed = now - start;
      setElapsedSec(elapsed / 1000);
      if (finishRef.current) {
        if (!finishStart) {
          finishStart = now;
          finishFrom = percentRef.current;
        }
        const k = Math.min(1, (now - finishStart) / 400);
        const p = finishFrom + (100 - finishFrom) * k;
        percentRef.current = p;
        setPercent(p);
        if (k >= 1 && !doneScheduled) {
          doneScheduled = true;
          setTimeout(() => setPhase("done"), 300);
        }
      } else {
        let p: number;
        if (elapsed <= 22000) p = 90 * (1 - Math.pow(1 - elapsed / 22000, 2));
        else p = 90 + 5 * Math.min(1, (elapsed - 22000) / 20000);
        percentRef.current = p;
        setPercent(p);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [phase]);

  /* Count the score up from 0 over ~800ms when results appear. */
  useEffect(() => {
    if (phase !== "done" || !result) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShownScore(result.score);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / 800);
      setShownScore(result.score * (1 - Math.pow(1 - k, 2)));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase, result]);

  /* Shared start path: the section's own form and the homepage hero bar both call this. */
  const runCheck = async (value: string) => {
    if (phase === "running") return;
    setUrl(value);
    if (!value.trim()) {
      track("website_check_error", { type: "invalid" });
      setError("invalid");
      setPhase("error");
      return;
    }
    track("website_check_start");
    setError(null);
    setResult(null);
    setPercent(0);
    percentRef.current = 0;
    finishRef.current = false;
    setElapsedSec(0);
    setPhase("running");
    try {
      const res = (await check({ data: { url: value } })) as CheckResponse;
      if (res.status === "error") {
        track("website_check_error", { type: res.errorType });
        setError(res.errorType);
        setPhase("error");
      } else {
        track("website_check_complete", { score: res.score, band: res.band });
        setResult(res);
        finishRef.current = true;
      }
    } catch {
      track("website_check_error", { type: "unreachable" });
      setError("unreachable");
      setPhase("error");
    }
  };

  const reset = () => {
    setPhase("idle");
    setResult(null);
    setError(null);
    setUrl("");
    setPercent(0);
    percentRef.current = 0;
    finishRef.current = false;
    setExpandedKey(null);
  };

  const tryAgain = () => {
    setPhase("idle");
    setError(null);
    setPercent(0);
    percentRef.current = 0;
    finishRef.current = false;
  };

  return {
    url,
    setUrl,
    phase,
    result,
    error,
    percent,
    elapsedSec,
    expandedKey,
    shownScore,
    runCheck,
    reset,
    tryAgain,
    setExpandedKey,
  };
}

/* Running state: progress ring, estimated time left and step list. */
export function WebsiteCheckProgress({
  url,
  percent,
  elapsedSec,
}: {
  url: string;
  percent: number;
  elapsedSec: number;
}) {
  const domain = domainOf(url);
  const secondsLeft = Math.max(0, Math.ceil(25 - elapsedSec));
  const slowMode = elapsedSec > 25;
  const stepState = (i: number): "done" | "running" | "waiting" => {
    if (percent >= STEP_THRESHOLDS[i] || percent >= 99) return "done";
    const firstPending = STEP_THRESHOLDS.findIndex((t, j) => percent < t && percent < 99);
    return i === firstPending ? "running" : "waiting";
  };

  return (
    <div className="flex flex-col items-center">
      <ProgressRing percent={percent} />
      <p className="mt-5 text-center text-[15px] text-[#c9d2df]">
        {slowMode ? (
          "Almost there, Google is finishing the speed test…"
        ) : (
          <>
            Scanning <strong className="font-semibold text-white">{domain}</strong>
            {" · "}
            {secondsLeft < 3 ? "a few seconds left" : `about ${secondsLeft} seconds left`}
          </>
        )}
      </p>
      <div className="mt-4 h-2 w-full max-w-[520px] overflow-hidden rounded-full bg-[rgba(255,255,255,0.08)]">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.min(100, percent)}%`,
            background: "linear-gradient(90deg,#e8a93a,#f4c873)",
          }}
        />
      </div>
      <ol className="mt-6 w-full max-w-[520px] space-y-2">
        {STEPS.map((step, i) => {
          const state = stepState(i);
          return (
            <li
              key={step}
              className="flex items-center gap-3 rounded-xl border px-4 py-3 text-sm"
              style={{
                background:
                  state === "running" ? "rgba(232,169,58,0.10)" : "rgba(255,255,255,0.04)",
                borderColor:
                  state === "running" ? "rgba(232,169,58,0.45)" : "rgba(255,255,255,0.07)",
                color: state === "waiting" ? "#8e9bb0" : "#ffffff",
              }}
            >
              <StepIcon state={state} />
              {state === "done" ? STEP_DONE_LABELS[i] : step}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* Error state with a try-again action. */
export function WebsiteCheckErrorPanel({
  errorType,
  onTryAgain,
}: {
  errorType: WebsiteCheckErrorType;
  onTryAgain: () => void;
}) {
  return (
    <div
      role="alert"
      className="max-w-[760px] rounded-xl border p-5 text-sm text-[#f8b4b4]"
      style={{
        borderColor: "rgba(248,113,113,0.4)",
        background: "rgba(248,113,113,0.08)",
      }}
    >
      <p>{ERROR_MESSAGES[errorType]}</p>
      <button
        type="button"
        onClick={onTryAgain}
        className={`mt-3 text-sm font-medium text-[#c9d2df] underline underline-offset-4 ${FOCUS_RING}`}
      >
        Try again
      </button>
    </div>
  );
}

/* Results state: score ring, band, per-check rows and the audit CTAs. */
export function WebsiteCheckResults({
  result,
  shownScore,
  expandedKey,
  onToggleKey,
  resultsNote = "This quick check covers 11 basics. Your full 30+ page audit goes much further, with dozens of additional checks on Google Maps visibility, practice pages, content, links and your top 25 competitors.",
  primaryCtaLabel = "Get my full free audit",
  secondaryLink,
  scrollTargetId = "audit-form",
  onReset,
}: {
  result: NonNullable<ReturnType<typeof useWebsiteCheck>["result"]>;
  shownScore: number;
  expandedKey: string | null;
  onToggleKey: (key: string | null) => void;
  resultsNote?: string;
  primaryCtaLabel?: string;
  secondaryLink?: { label: string; href: "/law-firm-seo-audit" };
  scrollTargetId?: string;
  onReset: () => void;
}) {
  const counts = {
    pass: result.checks.filter((c) => c.status === "pass").length,
    warn: result.checks.filter((c) => c.status === "warn").length,
    fail: result.checks.filter((c) => c.status === "fail").length,
  };
  const bandStyle =
    result.score >= 80
      ? { background: "rgba(74,222,128,0.15)", color: "#4ade80" }
      : result.score >= 50
        ? { background: "rgba(232,169,58,0.15)", color: "#f4c873" }
        : { background: "rgba(248,113,113,0.15)", color: "#f87171" };

  return (
    <div aria-live="polite">
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8">
        <ScoreRing score={result.score} shown={shownScore} />
        <div className="text-center sm:text-left">
          <span
            className="inline-block rounded-full px-4 py-1.5 text-[13px] font-semibold"
            style={bandStyle}
          >
            {result.band}
          </span>
          <p className="mt-3 break-all font-display text-[26px] font-semibold leading-tight text-white">
            {result.url}
          </p>
          <p className="mt-2 text-sm text-[#c9d2df]">
            {counts.pass} passed · {counts.warn} warnings · {counts.fail} failed
          </p>
        </div>
      </div>

      {!result.pagespeed && (
        <p className="mt-6 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.04)] p-4 text-sm text-[#c9d2df]">
          Speed results are temporarily unavailable. Here are the page checks we could run.
        </p>
      )}

      <ul className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        {result.checks.map((c) => {
          const expanded = expandedKey === c.key;
          return (
            <li key={c.key}>
              <button
                type="button"
                onClick={() => onToggleKey(expanded ? null : c.key)}
                aria-expanded={expanded}
                className={`group w-full rounded-xl border px-[14px] py-3 text-left ${FOCUS_RING}`}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  borderColor: "rgba(255,255,255,0.08)",
                }}
              >
                <span className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-white">{c.label}</span>
                  <span
                    className="text-sm font-bold"
                    style={{ color: VALUE_COLORS[c.status] }}
                  >
                    {c.status === "unknown" ? "Not measured" : c.value}
                  </span>
                </span>
                <span
                  className={`mt-1 block text-[13px] leading-relaxed text-[#c9d2df] ${expanded ? "block" : "hidden"} md:group-hover:block`}
                >
                  {EXPLANATIONS[c.key]}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <p className="mt-6 text-sm leading-relaxed text-[#c9d2df]">{resultsNote}</p>
      <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={() => scrollToAuditForm("website_check", scrollTargetId)}
          className={`inline-flex items-center justify-center gap-2 rounded-xl px-[26px] py-[18px] text-base font-bold text-[#0f1b2d] transition hover:-translate-y-px hover:brightness-105 motion-reduce:hover:translate-y-0 ${FOCUS_RING}`}
          style={{ background: "linear-gradient(180deg,#f2bb55,#e8a93a)" }}
        >
          {primaryCtaLabel}
          <span aria-hidden="true">→</span>
        </button>
        {secondaryLink && (
          <Link
            to={secondaryLink.href}
            className={`text-sm font-medium text-[#c9d2df] underline underline-offset-4 ${FOCUS_RING}`}
          >
            {secondaryLink.label}
          </Link>
        )}
        <button
          type="button"
          onClick={onReset}
          className={`text-sm font-medium text-[#c9d2df] underline underline-offset-4 ${FOCUS_RING}`}
        >
          Check another website
        </button>
      </div>
    </div>
  );
}

export function WebsiteCheck({
  heading = "Run a Free Instant Check on Your Law Firm Website",
  intro = "See how your website performs on the basics Google cares about. Enter your website address and we'll test mobile speed, Core Web Vitals, security and key page tags. It takes about 20 seconds and you stay on this page.",
  buttonLabel = "Run free check",
  smallPrint = "We test your homepage only. Results are not stored or shared.",
  resultsNote = "This quick check covers 11 basics. Your full 30+ page audit goes much further, with dozens of additional checks on Google Maps visibility, practice pages, content, links and your top 25 competitors.",
  primaryCtaLabel = "Get my full free audit",
  secondaryLink,
  scrollTargetId = "audit-form",
}: {
  heading?: string;
  intro?: string;
  buttonLabel?: string;
  smallPrint?: string;
  resultsNote?: string;
  primaryCtaLabel?: string;
  secondaryLink?: { label: string; href: "/law-firm-seo-audit" };
  scrollTargetId?: string;
}) {
  const wc = useWebsiteCheck();
  const { url, setUrl, phase, result, error, percent, elapsedSec, expandedKey, shownScore, runCheck, reset, tryAgain, setExpandedKey } = wc;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    await runCheck(url);
  };

  const headingWords = heading.trim().split(" ");
  const lastWord = headingWords.pop();

  return (
    <div
      className="relative overflow-hidden rounded-[22px] px-5 py-7 text-white md:px-16 md:py-14"
      style={{
        background:
          "radial-gradient(1200px 400px at 80% -10%, #24406a 0%, transparent 60%), linear-gradient(160deg, #0f1b2d, #13233a)",
      }}
    >
      {/* subtle 40px grid overlay */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="relative">
        <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(232,169,58,0.35)] bg-[rgba(232,169,58,0.12)] px-4 py-1.5 text-xs font-medium uppercase tracking-[0.14em] text-[#f4c873]">
          <span
            aria-hidden="true"
            className="h-2 w-2 rounded-full bg-[#e8a93a]"
            style={{ boxShadow: "0 0 10px 2px rgba(232,169,58,0.55)" }}
          />
          Free instant check · 20 seconds
        </span>

        <h2 className="mt-6 max-w-3xl text-[30px] font-semibold leading-[1.15] text-white md:text-[44px]">
          {headingWords.join(" ")} <span className="text-[#f4c873]">{lastWord}</span>
        </h2>
        <p className="mt-4 max-w-[640px] text-[17px] leading-relaxed text-[#c9d2df]">{intro}</p>

        <AutoHeight dep={phase}>
          {phase !== "done" && phase !== "running" && phase !== "error" && (
            <form onSubmit={submit} className="mt-8">
              <div
                className="flex max-w-[760px] flex-col gap-2 rounded-2xl bg-white p-2 sm:flex-row sm:items-center"
                style={{ boxShadow: "0 20px 50px rgba(0,0,0,0.35)" }}
              >
                <div className="flex flex-1 items-center gap-2 px-3">
                  <LockIcon />
                  <span aria-hidden="true" className="text-[15px] text-[#8e9bb0]">
                    https://
                  </span>
                  <label htmlFor="website-check-url" className="sr-only">
                    Your website address
                  </label>
                  <input
                    id="website-check-url"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    placeholder="yourlawfirm.com"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    className="w-full flex-1 bg-transparent py-3 text-[17px] text-[#0f1b2d] placeholder:text-[#8e9bb0] focus:outline-none"
                  />
                </div>
                <button
                  type="submit"
                  className={`inline-flex items-center justify-center gap-2 rounded-xl px-[26px] py-[18px] text-base font-bold text-[#0f1b2d] transition hover:-translate-y-px hover:brightness-105 motion-reduce:hover:translate-y-0 ${FOCUS_RING}`}
                  style={{ background: "linear-gradient(180deg,#f2bb55,#e8a93a)" }}
                >
                  {buttonLabel}
                  <span aria-hidden="true">→</span>
                </button>
              </div>
              <ul className="mt-5 flex max-w-[760px] flex-wrap gap-2">
                {FEATURE_CHIPS.map((chip, i) => (
                  <li
                    key={chip}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(255,255,255,0.14)] bg-[rgba(255,255,255,0.04)] px-3 py-1.5 text-[13px] text-[#c9d2df]"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#e8a93a"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d={CHIP_ICONS[i]} />
                    </svg>
                    {chip}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[13px] text-[#8e9bb0]">{smallPrint}</p>
            </form>
          )}

          {phase === "running" && (
            <div className="mt-8">
              <WebsiteCheckProgress url={url} percent={percent} elapsedSec={elapsedSec} />
            </div>
          )}

          {phase === "error" && error && (
            <div className="mt-8">
              <WebsiteCheckErrorPanel errorType={error} onTryAgain={tryAgain} />
            </div>
          )}

          {phase === "done" && result && (
            <div className="mt-10">
              <WebsiteCheckResults
                result={result}
                shownScore={shownScore}
                expandedKey={expandedKey}
                onToggleKey={setExpandedKey}
                resultsNote={resultsNote}
                primaryCtaLabel={primaryCtaLabel}
                secondaryLink={secondaryLink}
                scrollTargetId={scrollTargetId}
                onReset={reset}
              />
            </div>
          )}
        </AutoHeight>
      </div>
    </div>
  );
}
